import axios from 'axios';
import get from 'lodash/get';
import template from 'lodash/template';

import { BANNER_HEIGHT_DICT, POST_FORMAT, POST_PAGE_WIDTH_DICT } from '../../../../components/Blog/Post/constants';
import { convertToTree } from '../../../../components/Blog/utils';
import { POST_IMAGE_SELECTOR, VIDEO_WIDGET_SELECTOR } from '../../../constants';
import checkProjectWithAnimation from '../../../helpers/checkProjectWithAnimation';
import getStateValue from '../../../helpers/getStateValue';
import { addInViewWait, removeInViewWait } from '../../../helpers/inView';
import { updateMetaTags } from '../../../helpers/seo';
import dom from '../../../wrapper/DomWrapper';
import WidgetWrapper from '../../../wrapper/WidgetWrapper';
import lazyLoad from '../../LazyLoad';
import Video from '../../Video';
import PostImage from '../PostImage';
import SocialButtons from '../SocialButtons/SocialButtons';
import { prepareImages } from '../utils';

import getPlainExcerpt from './utils/getPlainExcerpt';
import Comments from './Comments';
import {
  COMMENTS_SELECTOR, HERO_SELECTOR, NAVIGATION_SELECTOR, POSTSOCIAL_SELECTOR, SOCIALBUTTONS_SELECTOR,
} from './constants';
import Hero from './Hero';
import Navigation from './Navigation';
import PostSocial from './PostSocial';

const getSinglePost = async (blogId, postUrl, isTemplate) => {
  const pageRenderService = getStateValue('apiUrls.pageRenderService');
  const projectId = getStateValue('projectId', '');

  const queryParameter = isTemplate ? `?templateLink=${projectId}` : '';

  const { data: post } = await axios.get(`${pageRenderService}/blog/${blogId}/post/${postUrl}${queryParameter}`);

  return post;
};

class Post extends WidgetWrapper {
  init = async () => {
    let dataPost = null;
    let dataBlog = null;

    const postDomNode = dom.getElement(this.selector);
    const isTemplate = getStateValue('isTemplate');

    try {
      const dataBlogRaw = postDomNode.dataset.settings;

      dataBlog = JSON.parse(dataBlogRaw);

      const { blogId } = dataBlog.blog;
      const urlSearchParams = new URLSearchParams(window.location.search);
      const pathnameArr = window.location.pathname.split('/').filter(Boolean);
      const params = Object.fromEntries(urlSearchParams.entries());
      const postUrl = process?.env?.IS_LOCAL ? get(params, 'postUrl', '') : pathnameArr[pathnameArr.length - 1];

      dataPost = await getSinglePost(blogId, postUrl, isTemplate);
    } catch (error) {
      console.warn(error);

      return null;
    }

    if (!dataPost) {
      return null;
    }

    this.withAnimation = checkProjectWithAnimation();

    const { blog, menu } = dataBlog;
    const settings = get(blog, 'settings', {});
    const postCommentsProvider = get(settings, 'postCommentsProvider', {});
    const disqusAccount = get(blog, 'disqusAccount');
    const { postPageSettings } = convertToTree(settings);
    const commentSettings = {
      postId: get(dataPost, ['postId']),
      postCommentsSortBy: get(dataPost, ['settings', 'postCommentsSortBy']),
      postCommentsPerPage: get(dataPost, ['settings', 'postCommentsPerPage']),
    };
    const { postPageFormat, postPageSocialSharing, postPageNavigation } = postPageSettings;
    const postPageComment = get(dataPost, ['settings', 'showPostComments'], false);
    const isShowSocialSharingButtons = get(dataPost, ['settings', 'showSocialSharingButtons'], false) && postPageSocialSharing;

    const {
      previewImageUrl,
      author,
      excerptOrigin,
      title,
      metaTitle = '',
      metaDescription = '',
      datePublished,
      // prev is nextUrl & next is prevUrl
      prevUrl: nextUrl,
      nextUrl: prevUrl,
      settings: {
        showCoverPhoto,
      },
      content,
      images,
      isStaticImagesPublished,
    } = dataPost;
    const finalPageFormat = postPageFormat === POST_FORMAT.HERO || showCoverPhoto
      ? POST_FORMAT.HERO
      : POST_FORMAT.STANDARD;
    const {
      postPageMeta,
      postPageAlign,
      postPageWidth,
      postPageMetaStyle,
      postPageBannerHeight,
    } = postPageSettings[finalPageFormat];

    const arrImages = prepareImages(images);
    const seoImage = arrImages.find(({ isSeo }) => !!isSeo);
    const previewImage = arrImages.find(({ isPreview }) => !!isPreview);

    updateMetaTags({
      type: 'article',
      title: metaTitle || title,
      description: metaDescription || getPlainExcerpt(excerptOrigin),
      image: seoImage || previewImage,
      isStaticImagesPublished,
    });

    const isStandardFormat = finalPageFormat === POST_FORMAT.STANDARD;
    const blogCategory = `blog-category-${POST_PAGE_WIDTH_DICT[postPageWidth]}`;
    const standardPostWrapper = isStandardFormat ? 'post-page__wrapper' : '';
    const standardGalleryWrap = isStandardFormat ? 'gallery-wrap-container' : '';
    const heroPostWrapper = !isStandardFormat ? 'post-page__wrapper' : '';
    const heroGalleryWrap = !isStandardFormat ? 'gallery-wrap-container' : '';

    const dataHero = {
      title,
      dateAdded: datePublished,
      author,
      bannerSrc: previewImageUrl,
      bannerHeight: BANNER_HEIGHT_DICT[postPageBannerHeight],
      meta: postPageMeta,
      metaStyle: postPageMetaStyle,
      align: postPageAlign,
      format: finalPageFormat,
      isStandardFormat,
      previewImage,
    };
    const dataComments = {
      postId: commentSettings.postId,
      countPerPage: commentSettings.postCommentsPerPage,
      sortBy: commentSettings.postCommentsSortBy,
      postCommentsProvider,
      disqusAccount,
      postPageComment,
      postPageCommentGlobal: settings.showPostComments,
    };
    const dataNavigation = {
      prev: prevUrl,
      next: nextUrl,
      menu,
    };

    const heroWidget = new Hero(HERO_SELECTOR);
    const commentsWidget = new Comments(COMMENTS_SELECTOR);
    const navigationWidget = postPageNavigation ? new Navigation(NAVIGATION_SELECTOR) : null;
    const postSocialWidget = new PostSocial(POSTSOCIAL_SELECTOR);
    const socialButtonsWidget = new SocialButtons(SOCIALBUTTONS_SELECTOR);
    const postImageWidget = new PostImage(POST_IMAGE_SELECTOR);
    const videoWidget = new Video(VIDEO_WIDGET_SELECTOR);

    const heroContent = heroWidget.getContent(dataHero, {
      layoutSettings: settings,
      isStaticImagesPublished,
    });
    const commentsContent = commentsWidget.getContent(dataComments);
    const postSocialContent = postSocialWidget.getContent(isShowSocialSharingButtons);
    const compiled = template(postDomNode.parentNode.innerHTML, {
      interpolate: /{{=([\S\s]+?)}}/g,
      evaluate: /{{([\S\s]+?)}}/g,
    });

    const parentPostDomNode = postDomNode.parentNode;

    parentPostDomNode.innerHTML = compiled({
      content,
      hero: heroContent,
      comments: commentsContent,
      blogCategory,
      standardPostWrapper,
      standardGalleryWrap,
      heroPostWrapper,
      heroGalleryWrap,
      socialButtons: postSocialContent,
    });

    if (postPageNavigation) {
      navigationWidget.init(dataNavigation);
    }

    socialButtonsWidget.init();
    postImageWidget.init();
    videoWidget.init();

    if (postPageComment) {
      commentsWidget.init(postCommentsProvider, postDomNode);
    }

    this.initAnimation(parentPostDomNode);

    parentPostDomNode.childNodes[0].style.display = 'none';
    parentPostDomNode.childNodes[1].style.visibility = 'visible';
    heroWidget.beforeInitLazy(parentPostDomNode);

    setTimeout(() => lazyLoad(), 0);

    this.updateAnimation(parentPostDomNode);
  };

  initAnimation = (elContainer) => {
    if (!this.withAnimation) return;

    this.elementsAnimation = [
      dom.getElement('.post-page__meta', elContainer),
      dom.getElement('.post-page__title', elContainer),
      ...dom.getCollection('.editor__block', elContainer) || [],
      ...dom.getCollection('.video-container', elContainer) || [],
      dom.getElement('.post-page__social', elContainer),
      dom.getElement('.post-page__comments', elContainer),
      dom.getElement('.post-page__navigation', elContainer),
    ].filter(Boolean);

    addInViewWait(this.elementsAnimation);
  };

  updateAnimation = () => {
    if (!this.withAnimation) return;

    removeInViewWait(this.elementsAnimation);
  };
}

export default Post;
